import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Ambulance, Plus, CheckCircle, Clock, XCircle, AlertTriangle, Filter, Users, RefreshCw,
} from 'lucide-react';
import useApiMethods from '../../lib/useApiMethods';
import { usePartnerPortal } from './PartnerPortalContext';
import { cn } from '../../utils/tailwindUtils';

const STATUS_OPTIONS = [
  { value: '',           label: 'All statuses' },
  { value: 'arrived',    label: 'Arrived' },
  { value: 'admitted',   label: 'Admitted' },
  { value: 'discharged', label: 'Discharged' },
  { value: 'cancelled',  label: 'Cancelled' },
];

const STATUS_STYLE = {
  arrived:    'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
  admitted:   'bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-300',
  discharged: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300',
  cancelled:  'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300',
};

const fmtINR = (n) => Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 });
const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';


export default function PartnerPortalPickupsList() {
  const navigate = useNavigate();
  const { who } = usePartnerPortal();
  const { fetch: apiFetch } = useApiMethods();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [query, setQuery] = useState('');
  const [refreshTick, setRefreshTick] = useState(0);

  useEffect(() => {
    if (who && who.kind === 'doctor') navigate('/partner-portal/referrals', { replace: true });
  }, [who, navigate]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const params = {};
        if (statusFilter) params.status = statusFilter;
        const resp = await apiFetch('engage/portal/ambulance/referrals/', params);
        if (cancelled) return;
        setRows(resp?.results || []);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, refreshTick]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(r => (
      (r.patient_name || '').toLowerCase().includes(q) ||
      (r.condition || '').toLowerCase().includes(q) ||
      (r.pickup_location || '').toLowerCase().includes(q)
    ));
  }, [rows, query]);

  const totals = useMemo(() => {
    const emergencies = rows.filter(r => r.is_emergency).length;
    const paid = rows.filter(r => r.incentive_paid).reduce((s, r) => s + Number(r.incentive_amount || 0), 0);
    const pending = rows.filter(r => !r.incentive_paid).reduce((s, r) => s + Number(r.incentive_amount || 0), 0);
    return { total: rows.length, emergencies, paid, pending };
  }, [rows]);

  return (
    <div className="p-5 max-w-[1200px] mx-auto space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-xl font-bold text-gray-900 dark:text-white">My Pickups</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">Every patient you've brought to the hospital, with live outcome.</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setRefreshTick(t => t + 1)}
            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-sm text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700"
            title="Refresh"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </button>
          <Link
            to="/partner-portal/pickups/new"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-gradient-to-r from-orange-500 to-red-600 text-white text-sm shadow-sm hover:from-orange-600 hover:to-red-700"
          >
            <Plus className="w-4 h-4" /> Record Pickup
          </Link>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <MiniStat icon={Users} label="Pickups" value={totals.total} loading={loading} tint="blue" />
        <MiniStat icon={AlertTriangle} label="Emergencies" value={totals.emergencies} loading={loading} tint="red" />
        <MiniStat icon={CheckCircle} label="Paid earnings" value={`₹ ${fmtINR(totals.paid)}`} loading={loading} tint="green" />
        <MiniStat icon={Clock} label="Pending" value={`₹ ${fmtINR(totals.pending)}`} loading={loading} tint="amber" />
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm">
        <div className="flex flex-wrap items-center gap-2 p-3 border-b border-gray-100 dark:border-gray-700">
          <div className="relative flex-1 min-w-[160px]">
            <input
              placeholder="Search by patient, condition, location…"
              value={query}
              onChange={e => setQuery(e.target.value)}
              className="w-full pl-3 pr-3 py-2 text-sm rounded-lg bg-gray-50 dark:bg-gray-900/40 border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-orange-500"
            />
          </div>
          <div className="inline-flex items-center gap-1.5">
            <Filter className="w-3.5 h-3.5 text-gray-400" />
            <select
              value={statusFilter}
              onChange={e => setStatusFilter(e.target.value)}
              className="px-2.5 py-2 text-sm rounded-lg bg-gray-50 dark:bg-gray-900/40 border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-white"
            >
              {STATUS_OPTIONS.map(o => <option key={o.value || 'all'} value={o.value}>{o.label}</option>)}
            </select>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 dark:bg-gray-900/40">
              <tr className="text-left text-[11px] uppercase tracking-wide text-gray-500 dark:text-gray-400">
                <th className="px-3 py-2 font-medium">Patient</th>
                <th className="px-3 py-2 font-medium">Condition</th>
                <th className="px-3 py-2 font-medium">Pickup</th>
                <th className="px-3 py-2 font-medium">Date</th>
                <th className="px-3 py-2 font-medium">Status</th>
                <th className="px-3 py-2 font-medium text-right">Incentive</th>
                <th className="px-3 py-2 font-medium">Payment</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
              {loading ? (
                <tr><td colSpan={7} className="px-3 py-8 text-center text-gray-400">Loading…</td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan={7} className="px-3 py-10 text-center text-gray-500">
                  {rows.length === 0 ? (
                    <div className="space-y-2">
                      <Ambulance className="w-8 h-8 mx-auto text-gray-300 dark:text-gray-600" />
                      <div>No pickups logged yet.</div>
                      <Link to="/partner-portal/pickups/new" className="inline-flex items-center gap-1.5 text-orange-600 hover:text-orange-700 text-sm font-medium">
                        <Plus className="w-4 h-4" /> Record your first pickup
                      </Link>
                    </div>
                  ) : 'No pickups match your filters.'}
                </td></tr>
              ) : filtered.map(r => (
                <tr key={r.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/40">
                  <td className="px-3 py-2.5">
                    <div className="font-medium text-gray-900 dark:text-white">{r.patient_name || '—'}</div>
                    {r.patient_mobile && <div className="text-[11px] text-gray-500 dark:text-gray-400">{r.patient_mobile}</div>}
                  </td>
                  <td className="px-3 py-2.5 text-[12px] text-gray-700 dark:text-gray-300 max-w-[220px]">
                    <div className="truncate">{r.condition || '—'}</div>
                    {r.is_emergency && <span className="text-[10px] text-red-500 font-medium">EMERGENCY</span>}
                  </td>
                  <td className="px-3 py-2.5 text-[12px] text-gray-600 dark:text-gray-300 max-w-[160px] truncate">{r.pickup_location || '—'}</td>
                  <td className="px-3 py-2.5 text-[12px] text-gray-600 dark:text-gray-300">{fmtDate(r.referral_date)}</td>
                  <td className="px-3 py-2.5">
                    <span className={cn('inline-block px-2 py-0.5 rounded-full text-[10px] font-medium', STATUS_STYLE[r.status] || 'bg-gray-100 text-gray-700')}>
                      {r.status}
                    </span>
                  </td>
                  <td className="px-3 py-2.5 text-right font-medium text-gray-900 dark:text-white">₹ {fmtINR(r.incentive_amount)}</td>
                  <td className="px-3 py-2.5">
                    {r.incentive_paid ? (
                      <span className="inline-flex items-center gap-1 text-[11px] text-green-700 dark:text-green-400 bg-green-50 dark:bg-green-900/20 px-2 py-0.5 rounded-full">
                        <CheckCircle className="w-3 h-3" /> Paid {r.incentive_paid_at ? `· ${fmtDate(r.incentive_paid_at)}` : ''}
                      </span>
                    ) : r.status === 'cancelled' ? (
                      <span className="inline-flex items-center gap-1 text-[11px] text-gray-500 dark:text-gray-400">
                        <XCircle className="w-3 h-3" /> N/A
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-[11px] text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-900/20 px-2 py-0.5 rounded-full">
                        <Clock className="w-3 h-3" /> Pending
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}


function MiniStat({ icon: Icon, label, value, loading, tint }) {
  const tintMap = {
    blue:  'bg-blue-50 text-blue-600 dark:bg-blue-900/20 dark:text-blue-400',
    green: 'bg-green-50 text-green-600 dark:bg-green-900/20 dark:text-green-400',
    amber: 'bg-amber-50 text-amber-600 dark:bg-amber-900/20 dark:text-amber-400',
    red:   'bg-red-50 text-red-600 dark:bg-red-900/20 dark:text-red-400',
  };
  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl p-3 border border-gray-100 dark:border-gray-700 shadow-sm">
      <div className="flex items-center gap-2">
        <div className={cn('w-7 h-7 rounded-lg flex items-center justify-center', tintMap[tint])}><Icon className="w-3.5 h-3.5" /></div>
        <span className="text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400 font-medium">{label}</span>
      </div>
      <div className="text-lg font-bold mt-1.5 text-gray-900 dark:text-white">
        {loading ? <span className="inline-block w-12 h-4 bg-gray-100 dark:bg-gray-700 rounded animate-pulse" /> : value}
      </div>
    </div>
  );
}
